// Signup functions
// Handles user registration with email/password
// First user automatically creates organization

import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, query, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { getAuth, getDB } from '../firebase/config';
import { mergeOrganizationSettings } from '../constants/organizationSettings';
import type { Organization, User } from '../models';
import { logAuth } from '../utils/audit';

interface SignupParams {
  email: string;
  password: string;
  displayName: string;
  organizationName: string;
}

interface SignupResult {
  success: boolean;
  user?: User;
  organization?: Organization;
  error?: string;
}

/**
 * Signs up a new user with email/password
 * If this is the first user, creates an organization automatically
 */
export async function signupWithEmail({
  email,
  password,
  displayName,
  organizationName,
}: SignupParams): Promise<SignupResult> {
  try {
    const auth = getAuth();
    const db = getDB();

    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Update display name
    if (displayName) {
      await updateProfile(firebaseUser, { displayName });
    }

    // Send email verification
    try {
      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(firebaseUser);
    } catch (err) {
      console.error('Failed to send verification email:', err);
      // Don't fail signup if verification email fails
    }

    // Check if this is the first user (no organizations exist)
    const orgsQuery = query(collection(db, 'organizations'));
    const orgsSnapshot = await getDocs(orgsQuery);
    const isFirstUser = orgsSnapshot.empty;

    let organization: Organization;
    let organizationId: string;

    if (isFirstUser) {
      // First user - create organization
      const orgData: Omit<Organization, 'id'> = {
        name: organizationName || `${displayName}'s Organization`,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        settings: mergeOrganizationSettings(),
      };

      const orgRef = await addDoc(collection(db, 'organizations'), orgData);
      organizationId = orgRef.id;
      organization = { id: organizationId, ...orgData };
    } else {
      // This shouldn't happen in normal flow, but handle gracefully
      throw new Error('Organization must be created or joined via invitation');
    }

    // Check if user document already exists (by UID)
    const userDocRef = doc(db, 'users', firebaseUser.uid);
    const existingUserDoc = await getDoc(userDocRef);
    
    if (existingUserDoc.exists()) {
      // User document already exists - update it instead of creating duplicate
      await updateDoc(userDocRef, {
        email: firebaseUser.email || email,
        displayName: displayName || firebaseUser.displayName || 'User',
        organizationId,
        role: isFirstUser ? 'admin' : existingUserDoc.data().role || 'member', // Preserve existing role unless first user
        updatedAt: Timestamp.now(),
      });
      const user: User = { id: firebaseUser.uid, ...existingUserDoc.data(), ...{ email: firebaseUser.email || email, displayName: displayName || firebaseUser.displayName || 'User', organizationId, role: isFirstUser ? 'admin' : existingUserDoc.data().role || 'member' } } as User;
      
      return {
        success: true,
        user,
        organization,
      };
    }

    // Create user document with Firebase Auth UID as document ID
    const userData: Omit<User, 'id'> = {
      email: firebaseUser.email || email,
      displayName: displayName || firebaseUser.displayName || 'User',
      organizationId,
      role: isFirstUser ? 'admin' : 'member', // First user is always admin
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    await setDoc(userDocRef, userData);
    const user: User = { id: firebaseUser.uid, ...userData };

    // Log signup action
    await logAuth(firebaseUser.uid, organizationId, 'signup', {
      email,
      displayName,
      organizationName,
      isFirstUser,
    });

    return {
      success: true,
      user,
      organization,
    };
  } catch (error) {
    console.error('Signup error:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
